import { Lock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeSelect } from '../capture/NativeSelect';
import { ClassProblemLine, useClassSetter } from '../detail/ClassControl';
import { classOf, INTERNAL_CLASSES, isClassFilterValue, NO_CLASS } from '../internal-class';
import type { ReportExpenseRow } from './types';

/** True when the note's rows carry the class: the caller is Gestione or one of its approvers. */
export function rowsClassified(rows: readonly ReportExpenseRow[]): boolean {
  return rows.some((row) => classOf(row) !== undefined);
}

/**
 * The approver's class on the decision page (U1 board 2.6): a short list per row, saved at once,
 * without opening the expense. Shown only where the row carries the class.
 */
export function RowClass({ row, name }: { readonly row: ReportExpenseRow; readonly name: string }) {
  const { t } = useTranslation('expenses-parity');
  const initial = classOf(row);
  const [saved, setSaved] = useState(initial ?? null);
  const setter = useClassSetter<object>(row.id, saved, (view) => setSaved(classOf(view) ?? null));
  if (initial === undefined) return null;
  return (
    <span className="flex flex-col gap-1">
      <NativeSelect
        aria-label={t('class.rowLabel', { expense: name })}
        className="min-h-11 w-28"
        value={setter.shown ?? NO_CLASS}
        disabled={setter.busy}
        onChange={(event) => {
          const value = event.target.value;
          if (!isClassFilterValue(value)) return;
          void setter.choose(value === NO_CLASS ? null : value);
        }}
      >
        <option value={NO_CLASS}>{t('class.none')}</option>
        {INTERNAL_CLASSES.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </NativeSelect>
      <ClassProblemLine problem={setter.problem} />
    </span>
  );
}

/** Above the rows: who sees the class column (board 2.6). */
export function RowClassHint({ rows }: { readonly rows: readonly ReportExpenseRow[] }) {
  const { t } = useTranslation('expenses-parity');
  if (!rowsClassified(rows)) return null;
  return (
    <p className="text-muted-foreground flex items-center gap-1 text-sm">
      <Lock className="size-3.5" aria-hidden />
      {t('class.approverHint')}
    </p>
  );
}
